// src/components/admin/payroll/hooks/usePayrollProcessing.js
// Custom hook for managing payroll processing state and async operations

import { useState, useCallback } from 'react';
import payrollService from '../../../../services/payroll.service';

const usePayrollProcessing = (period) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    // Start async processing
    const startProcessing = useCallback(async (batchSize = 50) => {
        if (!period) {
            setError('No period selected');
            return null;
        }

        setIsProcessing(true);
        setError(null);
        setResult(null);
        setProgress(null);

        try {
            const response = await payrollService.startAsyncProcessing(period, batchSize);

            if (response.success && response.jobId) {
                setJobId(response.jobId);
                return response.jobId;
            } else {
                throw new Error(response.error || 'Failed to start processing');
            }
        } catch (err) {
            console.error('Error starting processing:', err);
            setError(err.message);
            setIsProcessing(false);
            return null;
        }
    }, [period]);

    // Poll for progress
    const pollProgress = useCallback(async (activeJobId) => {
        if (!activeJobId) return;

        try {
            const response = await payrollService.getProcessingProgress(activeJobId);

            if (response.success && response.job) {
                setProgress(response.job);

                if (response.job.status === 'completed') {
                    setResult(response.job.summary);
                    setIsProcessing(false);
                    return { done: true, success: true, job: response.job };
                } else if (response.job.status === 'failed') {
                    setError(response.job.error || 'Processing failed');
                    setIsProcessing(false);
                    return { done: true, success: false, error: response.job.error };
                }

                return { done: false, job: response.job };
            }
        } catch (err) {
            console.error('Error polling progress:', err);
            // Don't stop polling on temporary errors
            return { done: false, error: err.message };
        }

        return { done: false };
    }, []);

    // Start processing and wait for completion
    const processAndWait = useCallback(async (batchSize = 50, onProgress) => {
        const activeJobId = await startProcessing(batchSize);

        if (!activeJobId) return { success: false, error: error };

        return new Promise((resolve) => {
            const poll = async () => {
                const result = await pollProgress(activeJobId);

                if (onProgress && result.job) {
                    onProgress(result.job);
                }

                if (result.done) {
                    resolve({
                        success: result.success,
                        job: result.job,
                        error: result.error
                    });
                } else {
                    setTimeout(poll, 1000);
                }
            };

            poll();
        });
    }, [startProcessing, pollProgress, error]);

    // Bulk validate employees
    const bulkValidate = useCallback(async (employeeIds, action, notes = '') => {
        try {
            const response = await payrollService.bulkValidateEmployees(employeeIds, action, notes, period);
            return response;
        } catch (err) {
            console.error('Error bulk validating:', err);
            throw err;
        }
    }, [period]);

    // Lock payroll period
    const lockPeriod = useCallback(async () => {
        if (!period) return { success: false, error: 'No period selected' };

        try {
            const response = await payrollService.lockPayroll(period);
            return response;
        } catch (err) {
            console.error('Error locking period:', err);
            return { success: false, error: err.message };
        }
    }, [period]);

    // Unlock payroll period
    const unlockPeriod = useCallback(async (reason) => {
        if (!period) return { success: false, error: 'No period selected' };

        try {
            const response = await payrollService.unlockPayroll(period, reason);
            return response;
        } catch (err) {
            console.error('Error unlocking period:', err);
            return { success: false, error: err.message };
        }
    }, [period]);

    // Reset state
    const reset = useCallback(() => {
        setIsProcessing(false);
        setJobId(null);
        setProgress(null);
        setError(null);
        setResult(null);
    }, []);

    return {
        isProcessing,
        jobId,
        progress,
        error,
        result,
        startProcessing,
        pollProgress,
        processAndWait,
        bulkValidate,
        lockPeriod,
        unlockPeriod,
        reset
    };
};

export default usePayrollProcessing;
